import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { lookupApi, LookupItem } from '../services/lookupApi';
import { driversApi, Driver } from '../services/driversApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Vehicles: React.FC = () => {
    const { t } = useLanguage();
    
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
    
    // Lookups data state
    const [proprietaires, setProprietaires] = useState<LookupItem[]>([]);
    const [fuelTypes, setFuelTypes] = useState<LookupItem[]>([]);
    const [affectations, setAffectations] = useState<LookupItem[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);

    const formatDateForInput = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState<Partial<Vehicle>>({
        registrationNumber: '',
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        fuelTypeId: undefined,
        transmission: 'manual',
        status: 'active',
        mileage: 0,
        proprietorId: undefined,
        affectationId: undefined,
        driverId: undefined,
        dateAffectation: undefined
    });

    const loadVehicles = async () => {
        setLoading(true);
        try {
            const response = await vehiclesApi.getAll();
            if (response.success) {
                setVehicles(response.data);
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Failed to load vehicles:', error);
            blobticMessage.error(t('common.messages.error') || 'Error', 'Failed to load vehicles');
        } finally {
            setLoading(false);
        }
    };

    const loadLookupsAndDrivers = async () => {
        try {
            const [propRes, fuelRes, affRes, drvRes] = await Promise.all([
                lookupApi.proprietaires.getAll(),
                lookupApi.fuelTypes.getAll(),
                lookupApi.affectations.getAll(),
                driversApi.getAll()
            ]);
            if (propRes.success) setProprietaires(propRes.data);
            if (fuelRes.success) setFuelTypes(fuelRes.data);
            if (affRes.success) setAffectations(affRes.data);
            if (drvRes.success) setDrivers(drvRes.data);
        } catch (error) {
            console.error('Failed to load lookup items or drivers:', error);
        }
    };

    useEffect(() => {
        loadVehicles();
        loadLookupsAndDrivers();
    }, []);

    const handleOpenDialog = (vehicle?: Vehicle) => {
        if (vehicle) {
            setEditingVehicle(vehicle);
            setFormData({
                ...vehicle,
                dateAffectation: vehicle.dateAffectation ? new Date(vehicle.dateAffectation) : undefined
            });
        } else {
            setEditingVehicle(null);
            setFormData({
                registrationNumber: '',
                brand: '',
                model: '',
                year: new Date().getFullYear(),
                fuelTypeId: fuelTypes.length > 0 ? fuelTypes[0].id : undefined,
                transmission: 'manual',
                status: 'active',
                mileage: 0,
                proprietorId: proprietaires.length > 0 ? proprietaires[0].id : undefined,
                affectationId: affectations.length > 0 ? affectations[0].id : undefined,
                driverId: undefined,
                dateAffectation: undefined
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            let response;
            if (editingVehicle) {
                response = await vehiclesApi.update(editingVehicle.id, formData);
            } else {
                response = await vehiclesApi.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success') || 'Success', t('common.messages.saved') || 'Saved successfully');
                setIsDialogOpen(false);
                loadVehicles();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving vehicle:', error);
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Error occurred');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.actions.deleteConfirm') || 'Are you sure you want to delete this?')) return;
        try {
            const response = await vehiclesApi.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success') || 'Success', t('common.messages.deleted') || 'Deleted successfully');
                loadVehicles();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Unknown error');
        }
    };

    const columns = [
        { id: 'registrationNumber', label: t('parc.vehicles.registration') || 'Immatriculation', minWidth: 110 },
        { id: 'brand', label: t('parc.vehicles.brand') || 'Marque', minWidth: 90 },
        { id: 'model', label: t('parc.vehicles.model') || 'Modèle', minWidth: 90 },
        { id: 'year', label: t('parc.vehicles.year') || 'Année', minWidth: 70 },
        { id: 'proprietor', label: 'Propriétaire', minWidth: 120 },
        { id: 'affectation', label: 'Affectation', minWidth: 120 },
        { id: 'driverName', label: 'Conducteur', minWidth: 130 },
        { id: 'fuelType', label: t('parc.vehicles.fuelType') || 'Carburant', minWidth: 90 },
        { id: 'mileage', label: 'Km total', minWidth: 90, format: (v: number) => v?.toLocaleString('fr-FR') },
        {
            id: 'status',
            label: t('parc.vehicles.status') || 'Statut',
            minWidth: 100,
            format: (value: string) => {
                if (value === 'active' || value === 'Actif') return 'Actif';
                if (value === 'maintenance' || value === 'En maintenance') return 'En maintenance';
                if (value === 'out_of_service' || value === 'Hors service') return 'Hors service';
                return value;
            }
        }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.vehicles.newVehicle') || 'Nouveau Véhicule'}
        </Button>
    );

    return (
        <GenericPage title={t('parc.vehicles.title') || 'Véhicules'} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={vehicles}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingVehicle ? (t('parc.vehicles.editVehicle') || 'Modifier Véhicule') : (t('parc.vehicles.newVehicle') || 'Nouveau Véhicule')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.vehicles.registration') || 'Immatriculation'}
                                value={formData.registrationNumber || ''}
                                onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.vehicles.status') || 'Statut'}</InputLabel>
                                <Select
                                    value={formData.status || 'active'}
                                    label={t('parc.vehicles.status') || 'Statut'}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <MenuItem value="active">Actif</MenuItem>
                                    <MenuItem value="maintenance">En maintenance</MenuItem>
                                    <MenuItem value="out_of_service">Hors service</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.vehicles.brand') || 'Marque'}
                                value={formData.brand || ''}
                                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.vehicles.model') || 'Modèle'}
                                value={formData.model || ''}
                                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.vehicles.year') || 'Année'}
                                value={formData.year || ''}
                                onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Kilométrage (km)"
                                value={formData.mileage || 0}
                                onChange={(e) => setFormData({ ...formData, mileage: Number(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Propriétaire</InputLabel>
                                <Select
                                    value={formData.proprietorId || ''}
                                    label="Propriétaire"
                                    onChange={(e) => setFormData({ ...formData, proprietorId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    {proprietaires.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>{p.lib}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Affectation</InputLabel>
                                <Select
                                    value={formData.affectationId || ''}
                                    label="Affectation"
                                    onChange={(e) => setFormData({ ...formData, affectationId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    {affectations.map((a) => (
                                        <MenuItem key={a.id} value={a.id}>{a.lib}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.vehicles.fuelType') || 'Carburant'}</InputLabel>
                                <Select
                                    value={formData.fuelTypeId || ''}
                                    label={t('parc.vehicles.fuelType') || 'Carburant'}
                                    onChange={(e) => setFormData({ ...formData, fuelTypeId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    {fuelTypes.map((ft) => (
                                        <MenuItem key={ft.id} value={ft.id}>{ft.lib}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.vehicles.transmission') || 'Transmission'}</InputLabel>
                                <Select
                                    value={formData.transmission || 'manual'}
                                    label={t('parc.vehicles.transmission') || 'Transmission'}
                                    onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                                >
                                    <MenuItem value="manual">Manuel</MenuItem>
                                    <MenuItem value="automatic">Automatique</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Conducteur</InputLabel>
                                <Select
                                    value={formData.driverId || ''}
                                    label="Conducteur"
                                    onChange={(e) => setFormData({ ...formData, driverId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    <MenuItem value=""><em>Aucun</em></MenuItem>
                                    {drivers.map((d) => (
                                        <MenuItem key={d.id} value={d.id}>{d.firstName} {d.lastName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date d'affectation"
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.dateAffectation)}
                                onChange={(e) => setFormData({ ...formData, dateAffectation: e.target.value ? new Date(e.target.value) : undefined })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Vehicles;
